import debounce from 'lodash-es/debounce';
import { EventToolbox } from 'client/utils/event-toolbox';

const lineItemIdsStorage = [];
const adNamesStorage = [];
const creativeIdsStorage = [];
const adTrackingDebounceTimer = 250;

const emptyStorage = () => {
  lineItemIdsStorage.length = 0;
  adNamesStorage.length = 0;
  creativeIdsStorage.length = 0;
};

export const fireDefaultEvent = debounce(() => {
  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: {
      action_category: 'system',
      action_name: 'ad_load',
      value: adNamesStorage.join(','),
      action_cause: 'action_completed',
      line_item_id: lineItemIdsStorage.join(','),
      creative_id: creativeIdsStorage.join(','),
    },
  });
  emptyStorage();
}, adTrackingDebounceTimer);

export const fireHeavyAdInterventionEvent = debounce((lineItemId, messageText) => {
  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: {
      action_category: 'system',
      action_name: 'show_content',
      subaction_name: 'ad_unload',
      action_cause: messageText,
      value: lineItemId,
    },
  });
}, adTrackingDebounceTimer);

export const handleAdLoad = event => {
  const { resetLineItemIdsStorage, lineItemId, adName, slotId } = event;
  if (resetLineItemIdsStorage) {
    emptyStorage();
  }
  lineItemIdsStorage.push(lineItemId || '-1');
  adNamesStorage.push(adName.replace(/,/g, '/'));
  creativeIdsStorage.push(slotId);
  fireDefaultEvent();
};

// ExCo video tracking functions
export const handleExcoSlotStart = playerId => {
  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: {
      action_category: 'system',
      action_name: 'ad_load',
      value: playerId,
      action_cause: 'exco_player_slot_start',
    },
  });
};

export const fireExcoAdEvent = (playerId, eventName, eventData = {}) => {
  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: {
      action_category: 'system',
      action_name: 'ad_load',
      subaction_name: eventName,
      value: playerId,
      action_cause: 'exco_player_ad_event',
      line_item_id: eventData.lineItemId || '-1',
      creative_id: eventData.creativeId || '-1',
    },
  });
};

export const fireExcoPlayerEvent = (playerId, eventName, eventData = {}) => {
  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: {
      action_category: 'system',
      action_name: eventName,
      value: playerId,
      action_cause: 'exco_player_player_event',
      line_item_id: eventData.lineItemId || '-1',
      creative_id: eventData.creativeId || '-1',
    },
  });
};

/*
 * ExCo video engagement handlers
 */
export const excoMap = new Map();

export const removeExcoPlayer = playerId => {
  if (excoMap.has(playerId)) {
    excoMap.delete(playerId);
  }
};

export const setExcoPlayerData = ({ playerId, playerApi, isPaused, userInitiatedPause }) => {
  excoMap.set(playerId, { playerId, playerApi, isPaused, userInitiatedPause });
};

export const updatePlayerData = ({ playerId, isPaused }) => {
  if (!excoMap.has(playerId)) {
    return;
  }
  const existing = excoMap.get(playerId);
  excoMap.set(playerId, { ...existing, isPaused });
};

export const excoPauseVideoOnModalOpen = () => {
  // User interacted with drawer/modal open method. NOTE: This function should only be called from the main thread in a synchronous context.

  /*
  Create a snapshot of entries before iteration, to avoid any potential issues with concurrent modifications
   */
  const entries = Array.from(excoMap.entries());
  // Iterate over the entries, and pause the video if it's playing and user did not initiate a pause.'
  for (const [key, value] of entries) {
    // Check if the player is initialized.
    if (!value.playerApi) continue; // eslint-disable-line no-continue
    if (!value.isPaused) {
      // when the video is playing and user opens the modal, pause the video.
      value.playerApi.pause();
      excoMap.set(key, { ...value, isPaused: true, userInitiatedPause: false });
    } else {
      // If the video was already paused when the modal is opened, then the user initiated a pause.
      excoMap.set(key, { ...value, userInitiatedPause: true });
    }
  }
};

export const excoPlayVideoOnModalClosed = () => {
  // User interacted with drawer/modal open method, and did not directly pausing the video. NOTE: This function should only be called from the main thread in a synchronous context.

  /*
  Create a snapshot of entries before iteration, to avoid any potential issues with concurrent modifications
   */
  const entries = Array.from(excoMap.entries());
  // Iterate over the entries, and keep the video paused if user did initiate a pause.
  for (const [key, value] of entries) {
    // Check if the player is initialized, and the video is paused and user did not initiate a pause. Only then resume the video, and set the user initiated pause flag to false.
    if (value.playerApi && value.isPaused && !value.userInitiatedPause) {
      // when the video is playing and user opens the modal, pause the video.
      value.playerApi.play();
      excoMap.set(key, { ...value, isPaused: false, userInitiatedPause: false });
    }
  }
};
